import type { MaybeRefOrGetter, Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, toValue, unref } from 'vue';
import { getApi } from './api';

export type ShipmentRouteBatchRow = {
  id: number;
  shipment_id: number;
  from_stop_id: number;
  to_stop_id: number;
  corridor_width_meters: number;
  route_options: Array<{
    name?: string | null;
    encoded_polyline: string;
    meta?: Record<string, unknown>;
  }>;
};

export function accountShipmentRoutesBatchQueryKey(
  accountId: number,
  shipmentIds: number[],
): readonly ['v2', 'account-shipment-routes', number, number[]] {
  const sorted = [...shipmentIds].sort((a, b) => a - b);
  return ['v2', 'account-shipment-routes', accountId, sorted];
}

async function loadAccountShipmentRoutesBatchData(
  accountId: number,
  shipmentIds: number[],
): Promise<ShipmentRouteBatchRow[]> {
  if (accountId == null || accountId === 0 || !shipmentIds?.length) {
    return [];
  }
  const { api, baseURL } = getApi();
  const sorted = [...shipmentIds].sort((a, b) => a - b);
  const res = await api.get(`/api/v2/accounts/${accountId}/shipment-routes`, {
    baseURL,
    params: { shipment_ids: sorted.join(',') },
  });
  return (res.data?.data ?? []) as ShipmentRouteBatchRow[];
}

function useGetAccountShipmentRoutesBatch(
  accountId: Ref<number | null | undefined> | number,
  shipmentIds: Ref<number[]> | number[],
  options: { enabled: MaybeRefOrGetter<boolean> },
) {
  return useQuery({
    queryKey: [
      'v2',
      'account-shipment-routes',
      () => unref(accountId),
      () => {
        const ids = unref(shipmentIds);
        return Array.isArray(ids) ? [...ids].sort((a, b) => a - b) : [];
      },
    ],
    queryFn: async (): Promise<ShipmentRouteBatchRow[]> => {
      const { api, baseURL } = getApi();
      const aid = unref(accountId);
      const ids = unref(shipmentIds);
      const res = await api.get(`/api/v2/accounts/${aid}/shipment-routes`, {
        baseURL,
        params: { shipment_ids: (ids ?? []).join(',') },
      });
      return (res.data?.data ?? []) as ShipmentRouteBatchRow[];
    },
    enabled: computed(() => {
      const aid = unref(accountId);
      const ids = unref(shipmentIds);
      const paramsOk =
        aid != null && aid !== 0 && Array.isArray(ids) && ids.length > 0;
      return paramsOk && toValue(options.enabled);
    }),
  });
}

async function ensureAccountShipmentRoutesBatchQueryData(
  queryClient: QueryClient,
  accountId: number,
  shipmentIds: number[],
): Promise<ShipmentRouteBatchRow[]> {
  return queryClient.ensureQueryData({
    queryKey: accountShipmentRoutesBatchQueryKey(accountId, shipmentIds),
    queryFn: () => loadAccountShipmentRoutesBatchData(accountId, shipmentIds),
  });
}

async function fetchAccountShipmentRoutesBatchQueryData(
  queryClient: QueryClient,
  accountId: number,
  shipmentIds: number[],
): Promise<ShipmentRouteBatchRow[]> {
  return queryClient.fetchQuery({
    queryKey: accountShipmentRoutesBatchQueryKey(accountId, shipmentIds),
    queryFn: () => loadAccountShipmentRoutesBatchData(accountId, shipmentIds),
    staleTime: 0,
  });
}

const getAccountShipmentRoutesBatch = Object.assign(
  useGetAccountShipmentRoutesBatch,
  {
    queryKey: accountShipmentRoutesBatchQueryKey,
    ensureQueryData: ensureAccountShipmentRoutesBatchQueryData,
    fetchQueryData: fetchAccountShipmentRoutesBatchQueryData,
  },
);

export default getAccountShipmentRoutesBatch;
